import type { ItemId } from './items.js';
import { HERBS, HERB_IDS, type HerbId } from './herblore.js';
import type { SkillId } from './skills.js';
import type { SupplyId, ToolLine } from './tools.js';

export type ResourceKind =
  | 'birch' | 'pine' | 'oak' | 'willow' | 'ironwood' | 'silverbark' | 'yew' | 'elder'
  | 'copper' | 'tin' | 'iron' | 'coal' | 'silver' | 'starsilver' | 'gold' | 'deepglass' | 'emberheart'
  | 'cobalt' | 'tungsten' | 'platinum' | 'blackiron' | 'glowstone' | 'iridium'
  | 'wreck' | 'pylon' | 'rails' | 'aircraft' | 'plating' | 'core'
  | 'shore' | 'river' | 'lake' | 'fen' | 'cove' | 'quarry' | 'tarn' | 'reservoir' | 'sump'
  | 'onions' | 'mushrooms' | 'thyme' | 'sand'
  | 'wild_yarrow' | 'wild_comfrey' | 'wild_feverfew' | 'wild_wormwood' | 'wild_valerian' | 'wild_mandrake' | 'wild_nightshade' | 'wild_sunpetal' | 'wild_ghostcap' | 'wild_dawnroot'
  | 'ash' | 'cedar' | 'ghostoak' | 'stormwood' | 'blackwood' | 'redwood' | 'glasswood' | 'ancientyew' | 'worldroot';

export interface ResourceDef {
  skill: SkillId;
  level: number;
  xp: number;
  item: ItemId;
  /** Verb shown in tooltips, e.g. "Chop". */
  verb: string;
  /** Progressive form for the action bar, e.g. "Chopping". */
  doing: string;
  noun: string;
  /** Chance that a successful gather empties the resource (0 = never). */
  deplete: number;
  /** Ticks until a depleted resource comes back. */
  respawnTicks: number;
  /** Which line of tool in the pack does the work (none for foraging). */
  tool?: ToolLine;
  /** Minimum tier in that line (2 = iron for hatchets and pickaxes). */
  minTier?: number;
  /** Used up, one per catch. */
  uses?: SupplyId;
  /** Ticks between gather attempts. */
  everyTicks: number;
  /** Fishing spots sit on water; everything else blocks a land tile. */
  onWater?: boolean;
  /** Rocks: how many ores a vein holds before it's spent (a rich vein holds twice as many). */
  vein?: [number, number];
  /** Trees: good chops before it falls, and logs left to saw from the fallen trunk. */
  trunk?: [number, number];
  buck?: [number, number];
  /** Trees that only give logs after dark (silverbark). */
  nightOnly?: boolean;
  /** Trees: only gives wood when it's overcast or raining (stormwood). */
  stormOnly?: boolean;
  /** One great tree everyone chops together; each who helped gets a share when it falls. */
  shared?: boolean;
  /** Visual family for the client. */
  family: 'tree' | 'rock' | 'spot' | 'forage';
  /** Old-world salvage, mined like a rock but drawn as what it is (a car, a pylon, rails...). */
  salvage?: boolean;
}

/** Trees no longer fall at random: each takes a number of good chops (`trunk`), then lies to be sawn up (`buck`). */
const tree = (level: number, xp: number, item: ItemId, noun: string, trunk: [number, number], buck: [number, number], respawnTicks: number, minTier?: number, extra: Partial<ResourceDef> = {}): ResourceDef =>
  ({ skill: 'forestry', level, xp, item, verb: 'Chop', doing: 'Chopping', noun, deplete: 0, respawnTicks, tool: 'axe', minTier, everyTicks: 2, family: 'tree', trunk, buck, ...extra });
const rock = (level: number, xp: number, item: ItemId, noun: string, respawnTicks: number, vein: [number, number], minTier?: number): ResourceDef =>
  ({ skill: 'mining', level, xp, item, verb: 'Mine', doing: 'Mining', noun, deplete: 1, respawnTicks, tool: 'pick', minTier, everyTicks: 2, family: 'rock', vein });
/** Salvage: stripped with a pickaxe, holds a few pieces, comes back (the wind uncovers more). */
const salvage = (level: number, xp: number, item: ItemId, noun: string, verb: string, doing: string, respawnTicks: number, vein: [number, number], minTier?: number): ResourceDef =>
  ({ ...rock(level, xp, item, noun, respawnTicks, vein, minTier), verb, doing, salvage: true });
/** Fishing spots: the real rules (which fish, which gear) are in fishing.ts; this entry only says "it's a spot". */
const spot = (noun: string): ResourceDef =>
  ({ skill: 'fishing', level: 1, xp: 0, item: 'raw_minnow', verb: 'Fish', doing: 'Fishing', noun, deplete: 0, respawnTicks: 0, tool: 'net', everyTicks: 3, onWater: true, family: 'spot' });

function wildHerbs() {
  const out: Record<string, ResourceDef> = {};
  for (const id of HERB_IDS) {
    const h = HERBS[id];
    out[`wild_${id}`] = { skill: 'herblore', level: h.clean, xp: Math.round(h.cleanXp * 1.5 * 10) / 10, item: `grimy_${id}` as ItemId, verb: 'Pick', doing: 'Picking', noun: `wild ${h.name.toLowerCase()}`, deplete: 0.45, respawnTicks: 90 + h.clean * 2, everyTicks: 3, family: 'forage' };
  }
  return out as Record<`wild_${HerbId}`, ResourceDef>;
}

export const RESOURCES: Record<ResourceKind, ResourceDef> = {
  birch: tree(1, 12, 'birch_log', 'birch', [3, 5], [2, 3], 20),
  pine: tree(10, 12, 'pine_log', 'pine', [4, 6], [2, 4], 33),
  oak: tree(20, 21, 'oak_log', 'oak', [5, 7], [3, 4], 50),
  willow: tree(26, 29, 'willow_log', 'willow', [5, 8], [3, 5], 55),
  ironwood: tree(32, 52, 'ironwood_log', 'ironwood', [6, 9], [3, 5], 75, 2, { everyTicks: 3 }),
  silverbark: tree(38, 76, 'silverbark_log', 'silverbark', [6, 9], [3, 5], 80, 2, { nightOnly: true }),
  yew: tree(44, 76, 'yew_log', 'hollow yew', [8, 12], [4, 6], 110, 3, { everyTicks: 4 }),
  elder: tree(50, 174, 'heartwood_log', 'elder heartwood', [60, 80], [0, 0], 1200, 4, { shared: true }),
  // the high trees: one every five levels to 95, each in at least two places. Each cuts at its own
  // pace (everyTicks): the harder the wood, the longer between bites.
  ash: tree(55, 86, 'ash_log', 'ash', [6, 9], [3, 5], 120, 6, { everyTicks: 4 }),
  cedar: tree(60, 97, 'cedar_log', 'cedar', [6, 9], [3, 5], 130, 6, { everyTicks: 5 }),
  ghostoak: tree(65, 102, 'ghostoak_log', 'ghost oak', [6, 9], [3, 5], 140, 7, { everyTicks: 5 }),
  stormwood: tree(70, 132, 'stormwood_log', 'stormwood', [6, 9], [3, 5], 150, 7, { everyTicks: 3, stormOnly: true }),
  blackwood: tree(75, 137, 'blackwood_log', 'blackwood', [7, 10], [3, 5], 160, 8, { everyTicks: 4 }),
  redwood: tree(80, 194, 'redwood_log', 'redwood', [8, 11], [4, 6], 180, 8, { everyTicks: 5 }),
  glasswood: tree(85, 214, 'glasswood_log', 'glasswood', [7, 10], [3, 5], 190, 9, { everyTicks: 5 }),
  ancientyew: tree(90, 255, 'ancientyew_log', 'ancient yew', [8, 11], [4, 6], 210, 9, { everyTicks: 3 }),
  worldroot: tree(95, 316, 'worldroot_log', 'worldroot', [9, 12], [4, 6], 240, 10, { everyTicks: 5 }),
  copper: rock(1, 10, 'copper_ore', 'copper', 16, [3, 6]),
  tin: rock(1, 10, 'tin_ore', 'tin', 16, [3, 6]),
  iron: rock(15, 16, 'iron_ore', 'iron', 30, [3, 6]),
  coal: rock(10, 11, 'coal', 'coal', 30, [4, 8]),
  silver: rock(25, 23, 'silver_ore', 'silver', 40, [3, 6]),
  starsilver: rock(30, 26, 'starsilver_ore', 'starsilver', 60, [2, 5], 2),
  gold: rock(40, 43, 'gold_ore', 'gold', 70, [2, 5]),
  deepglass: rock(45, 57, 'deepglass_ore', 'deepglass', 90, [2, 4], 3),
  emberheart: rock(50, 90, 'ember_ore', 'emberheart', 110, [2, 4], 4),
  cobalt: rock(55, 133, 'cobalt_ore', 'cobalt', 120, [2, 4], 6),
  tungsten: rock(65, 100, 'tungsten_ore', 'tungsten', 140, [2, 4], 7),
  platinum: rock(75, 285, 'platinum_ore', 'platinum', 160, [2, 3], 7),
  blackiron: rock(80, 305, 'blackiron_ore', 'black iron', 180, [2, 4], 8),
  glowstone: rock(85, 429, 'glowstone', 'glowstone', 200, [2, 3], 8),
  iridium: rock(90, 809, 'iridium_ore', 'iridium', 220, [2, 3], 9),
  wreck: salvage(5, 11, 'scrap_iron', 'car wreck', 'Strip', 'Stripping', 40, [3, 6]),
  pylon: salvage(20, 43, 'copper_wire', 'dead pylon', 'Strip wire from', 'Stripping wire from', 60, [4, 8], 1),
  rails: salvage(35, 28, 'rail_steel', 'old rails', 'Prise up', 'Prising up', 70, [3, 6], 2),
  aircraft: salvage(60, 81, 'titanium_scrap', 'aircraft wreck', 'Cut into', 'Cutting into', 140, [4, 7], 5),
  plating: salvage(70, 139, 'plating', 'Facility plating', 'Prise off', 'Prising off', 160, [3, 5], 6),
  core: salvage(95, 1238, 'core_alloy', 'Facility core', 'Break into', 'Breaking into', 260, [2, 4], 10),
  shore: spot('the shallows'),
  river: spot('the river'),
  lake: spot('the lake'),
  fen: spot('the fen'),
  cove: spot('the cove'),
  quarry: spot('the flooded quarry'),
  tarn: spot('the tarn'),
  reservoir: spot('the reservoir'),
  sump: spot('the sump'),
  onions: { skill: 'cooking', level: 1, xp: 8, item: 'wild_onion', verb: 'Pick', doing: 'Picking', noun: 'wild onions', deplete: 0.5, respawnTicks: 60, everyTicks: 3, family: 'forage' },
  mushrooms: { skill: 'cooking', level: 10, xp: 14, item: 'mushroom', verb: 'Pick', doing: 'Picking', noun: 'mushrooms', deplete: 0.5, respawnTicks: 80, everyTicks: 3, family: 'forage' },
  thyme: { skill: 'cooking', level: 20, xp: 22, item: 'thyme', verb: 'Pick', doing: 'Picking', noun: 'thyme', deplete: 0.5, respawnTicks: 100, everyTicks: 3, family: 'forage' },
  // sand for glass (Smithing): a dune by the water, scooped by hand. It shifts, but there is always more
  sand: { skill: 'smithing', level: 1, xp: 1, item: 'sand', verb: 'Scoop', doing: 'Scooping', noun: 'sand', deplete: 0.06, respawnTicks: 40, everyTicks: 2, family: 'forage' },
  // herbs growing wild: picked grimy, for Herblore
  ...wildHerbs(),
};

export const isResourceKind = (v: unknown): v is ResourceKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(RESOURCES, v);
